/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';

import { Subject, takeUntil } from 'rxjs';
import { addPoolIdFilterParams } from 'ng/modules/logs/utils/logs.utils';
import { TrafficLogsGraphStore } from './traffic-logs-graph.store';
import { ITrafficLogsStates, TrafficLogsStore } from '../traffic-logs.store';
import { TLogsGraphStateParams } from '../../../logs.types';
import { LogsRefreshService } from '../../../services/logs-refresh.service';
import * as l10n from './traffic-logs-graph.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description
 *      Main graph for logs page.
 *      This component houses the bar-graph at top of traffic-logs page, gotten from /logs/analytics
 *      api when request param 'step' is provided. Each bar represents number of logs (y-axis)
 *      for a given time period (x-axis), for a given duration (entire x-axis).
 *      Graph is made by child component avi-bar-graph, this component basically
 *      just handles api calls and passing in data to avi-bar-graph.
 * @author Yogesh Patil
 */
@Component({
    selector: 'traffic-logs-graph',
    templateUrl: './traffic-logs-graph.component.html',
})
export class TrafficLogsGraphComponent implements OnInit, OnDestroy {
    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    /**
     * The ID of the pool used to filter logs from a VS.
     * If not provided, all logs from the VS will be fetched.
     */
    @Input()
    public poolId?: string;

    /**
     * Unique label addendum to show for summary row inside tooltip.
     */
    public tooltipLogsLabel: string;

    private ngDestroyed$ = new Subject();

    constructor(
        public readonly trafficLogsGraphStore: TrafficLogsGraphStore,
        public readonly trafficLogsStore: TrafficLogsStore<ITrafficLogsStates>,
        private readonly l10nService: L10nService,
        logsRefreshService: LogsRefreshService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        // Subscribe to Refresh service for refresh graph action.
        logsRefreshService.refresh$.subscribe(({ isPageCustomTimeframe }) => {
            if (isPageCustomTimeframe) {
                trafficLogsGraphStore.refreshGraph();
            }
        });
    }

    /** @override */
    public ngOnInit(): void {
        /**
         * Subscribe to changes in the logs API parameters. Get new logs graph data upon change.
         * subscribe to the store under ngOnInit instead of the constructor to avoid the unnecessary API call triggered
         * by the emission before the store is first lazy initialized, which happens under the page component ngOnInit
         */
        this.trafficLogsStore.logsGraphApiParams$.pipe(
            takeUntil(this.ngDestroyed$),
        ).subscribe((apiParams: TLogsGraphStateParams) => {
            this.trafficLogsGraphStore.getLogsGraphData(
                addPoolIdFilterParams({ ...apiParams }, this.poolId),
            );
        });

        this.tooltipLogsLabel = this.l10nService.getMessage(globalL10nKeys.logsLabel);
    }

    /** @override */
    public ngOnDestroy(): void {
        this.trafficLogsGraphStore.cancelLogsGraphRequest();
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
    }
}
